import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Select, Input, Row, Col, Switch, theme } from 'antd'
import { PlusOutlined, MinusOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { StyledItem } from './styledComponents'
import { useTranslation } from 'react-i18next'
import TechnicalDrawingDownload from '$components/FileDownload/TechnicalDrawingDownload'
import Form from '$components/Form'
import { fetchPostProcessingMethodWithOamId } from '$api/evoAPIs'

const { TextArea } = Input
const { useToken } = theme

const DESCRIBABLE_METHODS = ['custom', 'painted', 'color_dyed']

const updateOptionDisabledState = (option, prevValue, newValue) => {
  if (option.value === prevValue) {
    return { ...option, disabled: false }
  }
  if (option.value === newValue) {
    return { ...option, disabled: true }
  }
  return option
}

const isMethodSelected = (methods, optionValue) =>
  methods.some((method) => method.post_processing_method === optionValue)

const setOptionDisabledFromMethods = (prevOptions, methods) =>
  prevOptions.map((option) => ({
    ...option,
    disabled: isMethodSelected(methods, option.value),
  }))

const initializeFieldsFromWizardData = (
  wizardData,
  recordId,
  setFields,
  setFieldValues,
  setOptions,
) => {
  const configData =
    wizardData?.configFormData?.selected_post_processing_methods
  if (!configData?.[recordId]) {
    return
  }

  const methods = configData[recordId]
  const initialFields = methods.map((_, index) => index)
  setFields(initialFields)

  const initialFieldValues = {}
  methods.forEach((method, index) => {
    initialFieldValues[index] = method.post_processing_method
  })
  setFieldValues(initialFieldValues)

  setOptions((prevOptions) =>
    setOptionDisabledFromMethods(prevOptions, methods),
  )
}

const ConfigOrderPartDetailView = ({ record, wizardData, form }) => {
  const { token } = useToken()
  const { t, i18n } = useTranslation()

  const [fields, setFields] = useState([])
  const [fieldValues, setFieldValues] = useState({})
  const [options, setOptions] = useState([])

  const selectedAdditiveMaterials = Form.useWatch(
    ['additive_materials', record.id],
    form,
  )

  const {
    data: selectedOamPostProcessingMethods,
    isLoading: selectedOamPostProcessingMethodsLoading,
  } = useQuery({
    queryKey: ['postProcessingMethods', selectedAdditiveMaterials],
    queryFn: () =>
      fetchPostProcessingMethodWithOamId(selectedAdditiveMaterials),
    enabled: !!selectedAdditiveMaterials,
  })

  useEffect(() => {
    if (selectedOamPostProcessingMethodsLoading) {
      return
    }
    const postProcessingMethods = selectedOamPostProcessingMethods || []

    setOptions(
      postProcessingMethods.map((method) => ({
        value: method,
        label: t(`service.order_part.follow_up_treatments.${method}`),
        disabled: false,
        displayDescriptionField: DESCRIBABLE_METHODS.includes(method),
      })),
    )
  }, [
    selectedOamPostProcessingMethodsLoading,
    selectedOamPostProcessingMethods,
    t,
    i18n.language,
  ])

  useEffect(() => {
    initializeFieldsFromWizardData(
      wizardData,
      record.id,
      setFields,
      setFieldValues,
      setOptions,
    )
  }, [wizardData, record.id])

  const handleChange = (value, fieldId) => {
    const prevValue = fieldValues?.[fieldId]
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }))

    setOptions((prev) =>
      prev.map((option) => updateOptionDisabledState(option, prevValue, value)),
    )
  }

  const handleAdd = () => {
    const newFieldId = fields.length > 0 ? Math.max(...fields) + 1 : 0
    setFields([...fields, newFieldId])

    const currentSelectedValues = new Set(
      Object.values(fieldValues).filter(Boolean),
    )
    setOptions((prev) =>
      prev.map((option) => ({
        ...option,
        disabled: currentSelectedValues.has(option.value),
      })),
    )
  }

  const handleRemove = (fieldId) => {
    const removedValue = fieldValues[fieldId]

    const newFields = fields.filter((f) => f !== fieldId)
    setFields(newFields)

    setFieldValues((prev) => {
      const { [fieldId]: _removed, ...rest } = prev
      return rest
    })

    setOptions((prev) =>
      prev.map((option) =>
        option.value === removedValue ? { ...option, disabled: false } : option,
      ),
    )

    const currentFormData =
      form.getFieldValue(['selected_post_processing_methods', record.id]) || []
    const fieldIndexToRemove = fields.indexOf(fieldId)

    const newFormData = currentFormData.filter(
      (_, idx) => idx !== fieldIndexToRemove,
    )

    form.setFieldValue(
      ['selected_post_processing_methods', record.id],
      newFormData,
    )
  }

  return (
    <div style={{ background: 'inherit' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '32%',
        }}
      >
        <h4>{t('service.order_part.follow_up_treatments.title')}</h4>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={options.filter((option) => !option.disabled).length === 0}
        >
          {t('service.order_part.add')}
        </Button>
      </div>

      {fields.length === 0 ? (
        <p style={{ color: token.colorTextDisabled }}>
          {t('service.order_part.follow_up_treatments.no_follow_up_treatment')}
        </p>
      ) : (
        fields.map((field, index) => {
          const selectedValue = fieldValues[field]
          const selectedOption = options.find(
            (option) => option.value === selectedValue,
          )

          return (
            <Row key={field}>
              <Col span={24}>
                <Row gutter={16}>
                  <Col span={8}>
                    <StyledItem
                      layout="vertical"
                      label={`${t('service.order_part.follow_up_treatments.label_select')} ${index + 1}`}
                      name={[
                        'selected_post_processing_methods',
                        record.id,
                        index,
                        'post_processing_method',
                      ]}
                    >
                      <Select
                        size="small"
                        onChange={(value) => handleChange(value, field)}
                        options={options}
                      />
                    </StyledItem>
                  </Col>

                  {selectedOption && selectedOption.displayDescriptionField && (
                    <Col span={8}>
                      <StyledItem
                        layout="vertical"
                        label={t(
                          `service.order_part.follow_up_treatments.description.${selectedOption.value}.label`,
                        )}
                        name={[
                          'selected_post_processing_methods',
                          record.id,
                          index,
                          'description',
                        ]}
                      >
                        <Input
                          size="small"
                          placeholder={t(
                            `service.order_part.follow_up_treatments.description.${selectedOption.value}.placeholder`,
                          )}
                        />
                      </StyledItem>
                    </Col>
                  )}
                  <div
                    key={field}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      marginBottom: '10px',
                    }}
                  >
                    <Button
                      shape="circle"
                      icon={<MinusOutlined />}
                      size="small"
                      onClick={() => handleRemove(field)}
                      style={{ marginLeft: '10px' }}
                    />
                  </div>
                </Row>
              </Col>
            </Row>
          )
        })
      )}

      <h4 style={{ paddingTop: '20px' }}>
        {t('service.order_part.extra_specifications')}
      </h4>
      <StyledItem
        labelAlign="left"
        colon={false}
        name={['config_order_part', record.id, 'maintain_alignment']}
        label={t('service.order_part.maintain_as_in_3d_file')}
        valuePropName="checked"
      >
        <Switch size="small" style={{ marginBottom: '2px' }} />
      </StyledItem>
      <StyledItem
        layout="vertical"
        label={`${t('service.order_part.extra_specifications')} :`}
        name={['config_order_part', record.id, 'extra_specifications']}
      >
        <TextArea
          showCount
          rows={4}
          maxLength={1000}
          placeholder={t('service.order_part.extra_specifications_placeholder')}
        />
      </StyledItem>
      <TechnicalDrawingDownload record={record} />
    </div>
  )
}

ConfigOrderPartDetailView.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  wizardData: PropTypes.shape({
    configFormData: PropTypes.shape({
      selected_post_processing_methods: PropTypes.objectOf(
        PropTypes.arrayOf(
          PropTypes.shape({
            post_processing_method: PropTypes.string,
            description: PropTypes.string,
          }),
        ),
      ),
    }),
  }).isRequired,
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
}

export default ConfigOrderPartDetailView
